import { useState, useEffect } from 'react'
import { X, User, Settings as SettingsIcon, Download, Upload, Trash2, Save } from 'lucide-react'
import { useAppStore } from '../store/useAppStore'
import { useTranslations } from '../hooks/useTranslations'
import * as api from '../api'

export default function Settings({ isOpen, onClose }) {
  const { user, logout, theme, setTheme, language, setLanguage } = useAppStore()
  const { t } = useTranslations()
  const [preferences, setPreferences] = useState({
    auto_save: 'true',
    export_quality: 'high',
    language: language
  })
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')

  // Reusable styles
  const styles = {
    fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    colors: {
      primary: theme === 'dark' ? '#f9fafb' : '#111827',
      secondary: theme === 'dark' ? '#9ca3af' : '#6b7280',
      background: theme === 'dark' ? '#1f2937' : '#ffffff',
      surface: theme === 'dark' ? '#374151' : '#f9fafb',
      border: theme === 'dark' ? '#4b5563' : '#e5e7eb',
      accent: {
        blue: '#3b82f6',
        green: '#10b981',
        purple: '#8b5cf6',
        orange: '#f59e0b',
        red: '#ef4444'
      }
    }
  }

  useEffect(() => {
    if (isOpen) {
      loadPreferences()
    }
  }, [isOpen])

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc)
      return () => document.removeEventListener('keydown', handleEsc)
    }
  }, [isOpen, onClose])

  const loadPreferences = async () => {
    try {
      // const prefs = await api.getUserPreferences()
      setPreferences(prefs)
    } catch (error) {
      console.error('Failed to load preferences:', error)
      // Keep default preferences if API fails
    }
  }

  const updatePreference = async (key, value) => {
    try {
      setIsLoading(true)
      // await api.updateUserPreferences({ [key]: value })
      setPreferences(prev => ({ ...prev, [key]: value }))
      setMessage('Settings saved successfully!')
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      setMessage('Failed to save settings')
      setTimeout(() => setMessage(''), 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: styles.colors.background,
          color: styles.colors.primary,
          borderRadius: '16px',
          boxShadow: theme === 'dark' 
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.1)' 
            : '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          width: '100%',
          maxWidth: '36rem',
          maxHeight: '85vh',
          overflow: 'auto',
          border: `1px solid ${styles.colors.border}`,
          position: 'relative',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          fontFamily: styles.fontFamily
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem 2rem',
          borderBottom: `1px solid ${styles.colors.border}`,
          backgroundColor: theme === 'dark' ? '#111827' : '#f8fafc',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '12px',
              backgroundColor: theme === 'dark' ? styles.colors.accent.blue : '#dbeafe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <SettingsIcon style={{ 
                width: '1.25rem', 
                height: '1.25rem', 
                color: theme === 'dark' ? '#ffffff' : '#1d4ed8'
              }} />
            </div>
            <div>
              <h2 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                color: styles.colors.primary,
                margin: 0,
                lineHeight: '1.4',
                fontFamily: styles.fontFamily
              }}>
                Settings
              </h2>
              <p style={{
                fontSize: '0.875rem',
                color: styles.colors.secondary,
                margin: '0.125rem 0 0 0',
                fontWeight: '400',
                fontFamily: styles.fontFamily
              }}>
                Manage your preferences and account
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '12px',
              border: 'none',
              background: styles.colors.surface,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              color: styles.colors.secondary
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#e5e7eb';
              e.target.style.color = theme === 'dark' ? '#d1d5db' : '#374151';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = styles.colors.surface;
              e.target.style.color = styles.colors.secondary;
            }}
            title="Close settings"
          >
            <X style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          {/* User Profile Section */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem',
              paddingBottom: '0.75rem',
              borderBottom: `1px solid ${styles.colors.border}`
            }}>
              <div style={{
                width: '2rem',
                height: '2rem',
                borderRadius: '8px',
                backgroundColor: theme === 'dark' ? styles.colors.accent.green : '#d1fae5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <User style={{ 
                  width: '1rem', 
                  height: '1rem', 
                  color: theme === 'dark' ? '#ffffff' : '#065f46'
                }} />
              </div>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: styles.colors.primary,
                margin: 0,
                fontFamily: styles.fontFamily
              }}>
                Account Information
              </h3>
            </div>
            
            <div style={{
              backgroundColor: styles.colors.surface,
              borderRadius: '12px',
              padding: '1.5rem',
              border: `1px solid ${styles.colors.border}`
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem 0',
                borderBottom: `1px solid ${styles.colors.border}`
              }}>
                <span style={{
                  color: styles.colors.secondary,
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  fontFamily: styles.fontFamily
                }}>Username</span>
                <span style={{
                  color: styles.colors.primary,
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  fontFamily: styles.fontFamily
                }}>{user?.username}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem 0',
                borderBottom: `1px solid ${styles.colors.border}`
              }}>
                <span style={{
                  color: styles.colors.secondary,
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  fontFamily: styles.fontFamily
                }}>Email</span>
                <span style={{
                  color: styles.colors.primary,
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  fontFamily: styles.fontFamily
                }}>{user?.email}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem 0',
                borderBottom: `1px solid ${styles.colors.border}`
              }}>
                <span style={{
                  color: styles.colors.secondary,
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  fontFamily: styles.fontFamily
                }}>User ID</span>
                <span style={{
                  color: styles.colors.secondary,
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  backgroundColor: theme === 'dark' ? '#1f2937' : '#f3f4f6',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px'
                }}>{user?.id}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem 0'
              }}>
                <span style={{
                  color: styles.colors.secondary,
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  fontFamily: styles.fontFamily
                }}>Storage Path</span>
                <span style={{
                  color: styles.colors.secondary,
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  backgroundColor: theme === 'dark' ? '#1f2937' : '#f3f4f6',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  maxWidth: '12rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }} title={user?.storage_path}>
                  {user?.storage_path}
                </span>
              </div>
            </div>
          </div>

          {/* Preferences Section */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem',
              paddingBottom: '0.75rem',
              borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#e5e7eb'}`
            }}>
              <div style={{
                width: '2rem',
                height: '2rem',
                borderRadius: '8px',
                backgroundColor: theme === 'dark' ? '#8b5cf6' : '#ede9fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <SettingsIcon style={{ 
                  width: '1rem', 
                  height: '1rem', 
                  color: theme === 'dark' ? '#ffffff' : '#7c3aed'
                }} />
              </div>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: theme === 'dark' ? '#f9fafb' : '#111827',
                margin: 0
              }}>
                Preferences
              </h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Theme Setting */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem',
                backgroundColor: theme === 'dark' ? '#374151' : '#f9fafb',
                borderRadius: '12px',
                border: `1px solid ${theme === 'dark' ? '#4b5563' : '#e5e7eb'}`
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#f9fafb' : '#111827',
                    marginBottom: '0.25rem'
                  }}>
                    Theme
                  </label>
                  <p style={{
                    fontSize: '0.75rem',
                    color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                    margin: 0
                  }}>
                    Choose your preferred interface theme
                  </p>
                </div>
                <select
                  value={theme}
                  onChange={(e) => {
                    setTheme(e.target.value)
                    updatePreference('theme', e.target.value)
                  }}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: `1px solid ${theme === 'dark' ? '#4b5563' : '#d1d5db'}`,
                    borderRadius: '8px',
                    backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                    color: theme === 'dark' ? '#f9fafb' : '#111827',
                    fontSize: '0.875rem',
                    minWidth: '8rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                </select>
              </div>

              {/* Auto Save Setting */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem',
                backgroundColor: theme === 'dark' ? '#374151' : '#f9fafb',
                borderRadius: '12px',
                border: `1px solid ${theme === 'dark' ? '#4b5563' : '#e5e7eb'}`
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#f9fafb' : '#111827',
                    marginBottom: '0.25rem'
                  }}>
                    Auto Save
                  </label>
                  <p style={{
                    fontSize: '0.75rem',
                    color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                    margin: 0
                  }}>
                    Automatically save your work
                  </p>
                </div>
                <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={preferences.auto_save === 'true'}
                    onChange={(e) => updatePreference('auto_save', e.target.checked.toString())}
                    style={{ position: 'absolute', opacity: 0, cursor: 'pointer', height: 0, width: 0 }}
                  />
                  <div style={{
                    width: '2.75rem',
                    height: '1.5rem',
                    backgroundColor: preferences.auto_save === 'true' ? '#3b82f6' : (theme === 'dark' ? '#4b5563' : '#d1d5db'),
                    borderRadius: '1rem',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    border: `2px solid ${preferences.auto_save === 'true' ? '#3b82f6' : (theme === 'dark' ? '#4b5563' : '#d1d5db')}`
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: preferences.auto_save === 'true' ? 'calc(100% - 18px)' : '2px',
                      width: '14px',
                      height: '14px',
                      backgroundColor: '#ffffff',
                      borderRadius: '50%',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }} />
                  </div>
                </label>
              </div>

              {/* Quality Setting */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem',
                backgroundColor: styles.colors.surface,
                borderRadius: '12px',
                border: `1px solid ${styles.colors.border}`
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: styles.colors.primary,
                    marginBottom: '0.25rem',
                    fontFamily: styles.fontFamily
                  }}>
                    Export Quality
                  </label>
                  <p style={{
                    fontSize: '0.75rem',
                    color: styles.colors.secondary,
                    margin: 0,
                    fontFamily: styles.fontFamily
                  }}>
                    Quality of exported images
                  </p>
                </div>
                <select
                  value={preferences.export_quality || 'high'}
                  onChange={(e) => updatePreference('export_quality', e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: `1px solid ${styles.colors.border}`,
                    borderRadius: '8px',
                    backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                    color: styles.colors.primary,
                    fontSize: '0.875rem',
                    minWidth: '8rem',
                    cursor: 'pointer',
                    fontFamily: styles.fontFamily
                  }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="max">Maximum</option>
                </select>
              </div>

              {/* Language Setting */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem',
                backgroundColor: styles.colors.surface,
                borderRadius: '12px',
                border: `1px solid ${styles.colors.border}`
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: styles.colors.primary,
                    marginBottom: '0.25rem',
                    fontFamily: styles.fontFamily
                  }}>
                    {t('voiceLanguage')}
                  </label>
                  <p style={{
                    fontSize: '0.75rem',
                    color: styles.colors.secondary,
                    margin: 0,
                    fontFamily: styles.fontFamily
                  }}>
                    {t('voiceLanguageDesc')}
                  </p>
                </div>
                <select
                  value={preferences.language || language}
                  onChange={(e) => {
                    const newLanguage = e.target.value;
                    updatePreference('language', newLanguage);
                    setLanguage(newLanguage);
                  }}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: `1px solid ${styles.colors.border}`,
                    borderRadius: '8px',
                    backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                    color: styles.colors.primary,
                    fontSize: '0.875rem',
                    minWidth: '8rem',
                    cursor: 'pointer',
                    fontFamily: styles.fontFamily
                  }}
                >
                  <option value="en">🇺🇸 English</option>
                  <option value="es">🇪🇸 Spanish</option>
                  <option value="ja">🇯🇵 Japanese</option>
                </select>
              </div>
            </div>
          </div>

          {/* Data Management Section */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem',
              paddingBottom: '0.75rem',
              borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#e5e7eb'}`
            }}>
              <div style={{
                width: '2rem',
                height: '2rem',
                borderRadius: '8px',
                backgroundColor: theme === 'dark' ? '#f59e0b' : '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Download style={{ 
                  width: '1rem', 
                  height: '1rem', 
                  color: theme === 'dark' ? '#ffffff' : '#d97706'
                }} />
              </div>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: theme === 'dark' ? '#f9fafb' : '#111827',
                margin: 0
              }}>
                Data Management
              </h3>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
              <button
                onClick={() => {
                  // TODO: Implement export functionality
                  setMessage('Export feature coming soon!')
                  setTimeout(() => setMessage(''), 3000)
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  border: `1px solid ${theme === 'dark' ? '#4b5563' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  backgroundColor: theme === 'dark' ? '#374151' : '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f9fafb';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme === 'dark' ? '#374151' : '#ffffff';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '8px',
                  backgroundColor: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Download style={{ width: '1.25rem', height: '1.25rem', color: '#1d4ed8' }} />
                </div>
                <div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#f9fafb' : '#111827',
                    marginBottom: '0.25rem'
                  }}>
                    Export Data
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                  }}>
                    Backup your sessions
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  // TODO: Implement import functionality
                  setMessage('Import feature coming soon!')
                  setTimeout(() => setMessage(''), 3000)
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  border: `1px solid ${theme === 'dark' ? '#4b5563' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  backgroundColor: theme === 'dark' ? '#374151' : '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f9fafb';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme === 'dark' ? '#374151' : '#ffffff';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '8px',
                  backgroundColor: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Upload style={{ width: '1.25rem', height: '1.25rem', color: '#16a34a' }} />
                </div>
                <div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#f9fafb' : '#111827',
                    marginBottom: '0.25rem'
                  }}>
                    Import Data
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                  }}>
                    Restore from backup
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  // TODO: Implement clear data functionality
                  if (confirm('Are you sure you want to clear all your data? This cannot be undone.')) {
                    setMessage('Clear data feature coming soon!')
                    setTimeout(() => setMessage(''), 3000)
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  border: `1px solid ${theme === 'dark' ? '#4b5563' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  backgroundColor: theme === 'dark' ? '#374151' : '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f9fafb';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme === 'dark' ? '#374151' : '#ffffff';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '8px',
                  backgroundColor: '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Trash2 style={{ width: '1.25rem', height: '1.25rem', color: '#dc2626' }} />
                </div>
                <div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#f9fafb' : '#111827',
                    marginBottom: '0.25rem'
                  }}>
                    Clear Data
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                  }}>
                    Delete all sessions
                  </div>
                </div>
              </button>

              <button
                onClick={handleLogout}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  border: `1px solid ${theme === 'dark' ? '#4b5563' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  backgroundColor: theme === 'dark' ? '#374151' : '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme === 'dark' ? '#4b5563' : '#f9fafb';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme === 'dark' ? '#374151' : '#ffffff';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '8px',
                  backgroundColor: '#fed7aa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <User style={{ width: '1.25rem', height: '1.25rem', color: '#ea580c' }} />
                </div>
                <div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: theme === 'dark' ? '#f9fafb' : '#111827',
                    marginBottom: '0.25rem'
                  }}>
                    Sign Out
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                  }}>
                    Logout from account
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Status Message */}
          {message && (
            <div style={{
              backgroundColor: theme === 'dark' ? '#1e3a8a' : '#dbeafe',
              border: `1px solid ${theme === 'dark' ? '#3b82f6' : '#93c5fd'}`,
              borderRadius: '12px',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <p style={{
                fontSize: '0.875rem',
                color: theme === 'dark' ? '#93c5fd' : '#1e40af',
                fontWeight: '500',
                margin: 0
              }}>
                {message}
              </p>
            </div>
          )}

          {/* Loading Indicator */}
          {isLoading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <div style={{
                width: '1.5rem',
                height: '1.5rem',
                border: `2px solid ${theme === 'dark' ? '#3b82f6' : '#3b82f6'}`,
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span style={{
                marginLeft: '0.5rem',
                fontSize: '0.875rem',
                color: theme === 'dark' ? '#9ca3af' : '#6b7280'
              }}>
                Saving...
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
